import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useCallback } from 'react';
import { useToast } from '@frontend/shadcn/hooks/use-toast';
import { InstagramCreatorAgeBucketInput, InstagramCreatorGender, InstagramCreatorCountry, InstagramCreatorInterest } from '@frontend/gql/social/graphql';
import { QueryVariables } from '../types';

// Define the schema for the filters form
export const filtersFormSchema = z.object({
  genders: z.array(z.string()).optional().default([]),
  query: z.string().optional().default(''),
  ageBuckets: z.array(z.string()).optional().default([]),
  countries: z.array(z.string()).optional().default([]),
  minFollowers: z.number().nullable().optional().default(null),
  maxFollowers: z.number().nullable().optional().default(null),
  minEngagements: z.number().nullable().optional().default(null),
  maxEngagements: z.number().nullable().optional().default(null),
  interests: z.array(z.string()).optional().default([]),
  majorAudienceAgeBuckets: z.array(z.string()).optional().default([]),
  majorAudienceGenders: z.array(z.string()).optional().default(['ALL']),
  majorAudienceCountries: z.array(z.string()).optional().default([]),
  // We'll add more filters here later as needed
}).refine(
  (data) => {
    // Handle followers validation
    if (data.minFollowers !== null && data.maxFollowers !== null) {
      // Both values are set
      if (data.minFollowers === 0 || data.maxFollowers === 0) {
        // Allow both values to be zero
        return true;
      }
      // For non-zero values, ensure minFollowers < maxFollowers
      return data.minFollowers < data.maxFollowers;
    } else if (data.minFollowers !== null) {
      return data.minFollowers > 0;
    } else if (data.maxFollowers !== null) {
      return data.maxFollowers > 0;
    }
    return true;
  },
  {
    message: 'Minimum followers cannot be greater than maximum followers',
    path: ['minFollowers']
  }
).refine(
  (data) => {
    // Handle engagements validation
    if (data.minEngagements !== null && data.maxEngagements !== null) {
      // Both values are set
      if (data.minEngagements === 0 || data.maxEngagements === 0) {
        // Allow both values to be zero
        return true;
      }
      // For non-zero values, ensure minEngagements < maxEngagements
      return data.minEngagements < data.maxEngagements;
    }
    if (data.minEngagements !== null) {
      return data.minEngagements > 0;
    }
    if (data.maxEngagements !== null) {
      return data.maxEngagements > 0;
    }
    return true;
  },
  {
    message: 'Minimum engagements cannot be greater than maximum engagements',
    path: ['minEngagements']
  }
);

export type FiltersFormData = z.infer<typeof filtersFormSchema>;

type UseFiltersFormProps = {
  defaultValues?: Partial<FiltersFormData>;
  onSubmit?: (data: Partial<QueryVariables>) => void;
};

// Helper function to cast form data to QueryVariables type
export const castFormDataToQueryVariables = (data: FiltersFormData): Partial<QueryVariables> => ({
  ...data,
  ageBuckets: data.ageBuckets as InstagramCreatorAgeBucketInput[],
  genders: data.genders as (InstagramCreatorGender | 'ALL')[],
  countries: data.countries as InstagramCreatorCountry[],
  interests: data.interests as InstagramCreatorInterest[],
  majorAudienceAgeBuckets: data.majorAudienceAgeBuckets as InstagramCreatorAgeBucketInput[],
  majorAudienceGenders: data.majorAudienceGenders as (InstagramCreatorGender | 'ALL')[],
  majorAudienceCountries: data.majorAudienceCountries as InstagramCreatorCountry[],
  before: null,
  after: null,
});

export const useFiltersForm = ({ defaultValues = {}, onSubmit }: UseFiltersFormProps = {}) => {
  const { toast } = useToast();

  // Initialize the form with React Hook Form
  const form = useForm<FiltersFormData>({
    resolver: zodResolver(filtersFormSchema),
    defaultValues: {
      genders: defaultValues.genders || [],
      query: defaultValues.query || '',
      ageBuckets: defaultValues.ageBuckets || [],
      countries: defaultValues.countries || [],
      minFollowers: defaultValues.minFollowers || null,
      maxFollowers: defaultValues.maxFollowers || null,
      minEngagements: defaultValues.minEngagements || null,
      maxEngagements: defaultValues.maxEngagements || null,
      interests: defaultValues.interests || [],
      majorAudienceAgeBuckets: defaultValues.majorAudienceAgeBuckets || [],
      majorAudienceGenders: defaultValues.majorAudienceGenders || ['ALL'],
      majorAudienceCountries: defaultValues.majorAudienceCountries || [],
    },
  });

  // Handle form submission
  const handleSubmit = useCallback((data: FiltersFormData) => {
    // Cast form data to QueryVariables type
    const typedData = castFormDataToQueryVariables(data);
    // Call the onSubmit callback if provided
    if (onSubmit) {
      onSubmit(typedData);
    }
  }, [onSubmit]);

  // Handle form errors
  const handleError = useCallback(() => {
    toast({
      variant: 'destructive',
      title: 'Validation Error',
      description: 'Please check your form inputs and try again.'
    });
  }, [toast]);

  // Submit wrapper that handles both success and error cases
  const submitForm = useCallback(() => {
    return form.handleSubmit(handleSubmit, handleError)();
  }, [form, handleSubmit, handleError]);

  return {
    form,
    submitForm,
    setValue: form.setValue,
  };
};
